import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { MainLayout } from '../components/Layout';
import api from '../services/api';
import { 
  FileText, Download, Clock, CheckCircle, XCircle, RefreshCw, Filter,
  BarChart3, FileSpreadsheet, Table2, Layout, Calendar, ChevronRight,
  Loader2, AlertCircle, Trash2, Eye, TrendingUp, Package, DollarSign,
  Users, Truck, FileCheck, PieChart
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mapeamento de ícones por categoria
const CATEGORY_ICONS = {
  VENDAS: TrendingUp,
  FINANCEIRO: DollarSign,
  FISCAL: FileCheck,
  ESTOQUE: Package,
  CMV: PieChart,
  CAIXA: DollarSign,
  DELIVERY: Truck,
  PRODUCAO: Package,
  CLIENTES: Users,
  GESTAO: Layout,
};

// Componente de Card de Relatório em Andamento
const ReportJobCard = ({ job, onView, onCancel }) => {
  const getStatusBadge = (status) => {
    const configs = {
      PENDING: { variant: 'secondary', icon: Clock, text: 'Aguardando', color: 'bg-yellow-100 text-yellow-800' },
      PROCESSING: { variant: 'secondary', icon: Loader2, text: 'Processando', color: 'bg-blue-100 text-blue-800', animate: true },
      SUCCESS: { variant: 'default', icon: CheckCircle, text: 'Concluído', color: 'bg-green-100 text-green-800' },
      ERROR: { variant: 'destructive', icon: XCircle, text: 'Erro', color: 'bg-red-100 text-red-800' },
      CANCELLED: { variant: 'secondary', icon: XCircle, text: 'Cancelado', color: 'bg-gray-100 text-gray-800' }
    };
    const config = configs[status] || configs.PENDING;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className={`w-3 h-3 ${config.animate ? 'animate-spin' : ''}`} />
        {config.text}
      </span>
    );
  };

  const getReportTypeName = (type) => {
    const names = {
      // VENDAS
      vendas_periodo: 'Vendas por Período',
      vendas_produto: 'Vendas por Produto',
      vendas_detalhado: 'Vendas Detalhado Completo',
      vendas_pagamento: 'Vendas por Forma de Pagamento',
      // CMV / CARDÁPIO
      performance_cardapio: 'Performance de Cardápio',
      curva_abc: 'Curva ABC',
      classificacao_cardapio: 'Classificação de Cardápio',
      // FINANCEIRO
      fluxo_caixa: 'Fluxo de Caixa',
      financeiro_consolidado: 'Resumo Financeiro Consolidado',
      // ESTOQUE
      posicao_estoque: 'Posição de Estoque',
      estoque_consumo: 'Consumo de Estoque',
      consumo_periodo: 'Consumo por Período',
      // FISCAL
      nf_geradas: 'Notas Fiscais Geradas',
      nf_emitidas: 'NF Emitidas',
      // DELIVERY
      entregas_motoboy: 'Entregas por Motoboy',
      cod_resumo: 'Controle COD - Pagamento na Entrega'
    };
    return names[type] || type;
  };

  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`job-card-${job.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="font-medium text-gray-900">{getReportTypeName(job.report_type)}</h4>
              {getStatusBadge(job.status)}
            </div>
            <p className="text-sm text-gray-500 mb-1">
              Período: {format(new Date(job.date_start), 'dd/MM/yyyy', { locale: ptBR })} - {format(new Date(job.date_end), 'dd/MM/yyyy', { locale: ptBR })}
            </p>
            <p className="text-xs text-gray-400">
              Criado em: {format(new Date(job.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
            </p>
            {job.records_count !== null && (
              <p className="text-xs text-gray-500 mt-1">
                {job.records_count} registros • {job.processing_time_ms}ms
              </p>
            )}
            {job.error_message && (
              <p className="text-xs text-red-500 mt-1">
                Erro: {job.error_message}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {job.status === 'SUCCESS' && job.file_url && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onView(job)}
                data-testid={`view-report-${job.id}`}
              >
                <Eye className="w-4 h-4 mr-1" />
                Ver
              </Button>
            )}
            {['PENDING', 'PROCESSING'].includes(job.status) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onCancel(job.id)}
                data-testid={`cancel-report-${job.id}`}
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Componente Principal
export default function ReportsPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('relatorios');
  const [categories, setCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('VENDAS');
  const [selectedReport, setSelectedReport] = useState('vendas_periodo');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Filtros do relatório
  const [dateStart, setDateStart] = useState(format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'));
  const [dateEnd, setDateEnd] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [exportFormat, setExportFormat] = useState('HTML');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Carregar categorias
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/api/reports/categories');
        setCategories(response.data.categories || {});
      } catch (error) {
        console.error('Erro ao carregar categorias:', error);
        toast.error('Erro ao carregar categorias de relatórios');
      }
    };
    
    if (token) {
      fetchCategories();
    }
  }, [token]);

  // Carregar jobs
  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/reports/jobs?limit=50');
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Erro ao carregar relatórios:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchJobs();
      // Auto-refresh a cada 5 segundos se houver jobs em processamento
      const interval = setInterval(() => {
        const hasProcessing = jobs.some(j => ['PENDING', 'PROCESSING'].includes(j.status));
        if (hasProcessing) {
          fetchJobs();
        }
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [token, jobs.length]);

  // Gerar relatório
  const handleGenerateReport = async () => {
    if (!dateStart || !dateEnd) {
      toast.error('Preencha as datas inicial e final');
      return;
    }

    const startDate = new Date(dateStart);
    const endDate = new Date(dateEnd);
    
    if (startDate > endDate) {
      toast.error('Data inicial não pode ser maior que data final');
      return;
    }

    const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    if (daysDiff > 365) {
      toast.error('Período máximo permitido é de 365 dias');
      return;
    }

    try {
      setGenerating(true);
      const response = await api.post('/api/reports/generate', {
        report_type: selectedReport,
        date_start: `${dateStart}T00:00:00Z`,
        date_end: `${dateEnd}T23:59:59Z`,
        format: exportFormat,
        filters: categoryFilter ? { category_id: categoryFilter } : null
      });

      toast.success('Relatório adicionado à fila de processamento');
      setActiveTab('andamento');
      fetchJobs();
    } catch (error) {
      const message = error.response?.data?.detail?.message || error.response?.data?.detail || 'Erro ao gerar relatório';
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  // Cancelar relatório
  const handleCancelReport = async (jobId) => {
    try {
      await api.delete(`/api/reports/jobs/${jobId}`);
      toast.success('Relatório cancelado');
      fetchJobs();
    } catch (error) {
      toast.error('Erro ao cancelar relatório');
    }
  };

  // Visualizar relatório
  const handleViewReport = (job) => {
    // Usar URL com token para download sem autenticação JWT
    const downloadUrl = job.file_url_with_token || job.file_url;
    if (downloadUrl) {
      window.open(`${API_URL}${downloadUrl}`, '_blank');
    }
  };

  // Selecionar categoria e relatório
  const handleCategoryClick = (category, reportCode = null) => {
    setSelectedCategory(category);
    if (reportCode) {
      setSelectedReport(reportCode);
    } else if (categories[category]?.reports?.length > 0) {
      setSelectedReport(categories[category].reports[0].code);
    }
  };

  // Obter nome do relatório selecionado
  const getSelectedReportName = () => {
    const cat = categories[selectedCategory];
    if (cat) {
      const report = cat.reports.find(r => r.code === selectedReport);
      return report?.name || selectedReport;
    }
    return selectedReport;
  };

  // Filtrar jobs por status
  const inProgressJobs = jobs.filter(j => ['PENDING', 'PROCESSING'].includes(j.status));
  const completedJobs = jobs.filter(j => j.status === 'SUCCESS');
  const errorJobs = jobs.filter(j => j.status === 'ERROR');

  return (
    <MainLayout>
    <div className="min-h-screen bg-gray-50" data-testid="reports-page">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900">Relatórios</h1>
          <p className="text-gray-500 text-sm mt-1">Gere e exporte relatórios do seu negócio</p>
        </div>
        
        {/* Top Navigation */}
        <div className="px-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-transparent border-b-0">
              <TabsTrigger 
                value="relatorios" 
                className="data-[state=active]:border-b-2 data-[state=active]:border-red-500 data-[state=active]:text-red-600 rounded-none"
                data-testid="tab-relatorios"
              >
                <FileText className="w-4 h-4 mr-2" />
                Relatórios
              </TabsTrigger>
              <TabsTrigger 
                value="graficos" 
                className="data-[state=active]:border-b-2 data-[state=active]:border-red-500 data-[state=active]:text-red-600 rounded-none"
                data-testid="tab-graficos"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Gráficos
              </TabsTrigger>
              <TabsTrigger 
                value="excel" 
                className="data-[state=active]:border-b-2 data-[state=active]:border-red-500 data-[state=active]:text-red-600 rounded-none"
                data-testid="tab-excel"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel
              </TabsTrigger>
              <TabsTrigger 
                value="dashboard" 
                className="data-[state=active]:border-b-2 data-[state=active]:border-red-500 data-[state=active]:text-red-600 rounded-none"
                data-testid="tab-dashboard"
              >
                <Layout className="w-4 h-4 mr-2" />
                Dashboard
              </TabsTrigger>
              <TabsTrigger 
                value="andamento" 
                className="data-[state=active]:border-b-2 data-[state=active]:border-red-500 data-[state=active]:text-red-600 rounded-none"
                data-testid="tab-andamento"
              >
                <Clock className="w-4 h-4 mr-2" />
                Em Andamento
                {inProgressJobs.length > 0 && (
                  <Badge variant="destructive" className="ml-2 px-1.5 py-0.5 text-xs">
                    {inProgressJobs.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Content */}
      <div className="flex">
        {/* Main Content */}
        <div className="flex-1 p-6">
          {activeTab === 'relatorios' && (
            <div className="space-y-6">
              {/* Seleção de Categoria e Tipo de Relatório */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Categoria do Relatório</Label>
                  <Select value={selectedCategory} onValueChange={(val) => handleCategoryClick(val)}>
                    <SelectTrigger data-testid="category-select">
                      <SelectValue placeholder="Selecione a categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(categories).map(([key, cat]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            {React.createElement(CATEGORY_ICONS[key] || FileText, { className: "w-4 h-4" })}
                            {cat.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tipo de Relatório</Label>
                  <Select value={selectedReport} onValueChange={setSelectedReport}>
                    <SelectTrigger data-testid="report-type-select">
                      <SelectValue placeholder="Selecione o relatório" />
                    </SelectTrigger>
                    <SelectContent>
                      {(categories[selectedCategory]?.reports || []).map(report => (
                        <SelectItem key={report.code} value={report.code}>
                          {report.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Card de Geração */}
              <Card data-testid="report-generator-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-red-500" />
                  {getSelectedReportName()}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Período */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date-start">Data Inicial *</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        id="date-start"
                        type="date"
                        value={dateStart}
                        onChange={(e) => setDateStart(e.target.value)}
                        className="pl-10"
                        data-testid="date-start-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date-end">Data Final *</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        id="date-end"
                        type="date"
                        value={dateEnd}
                        onChange={(e) => setDateEnd(e.target.value)}
                        className="pl-10"
                        data-testid="date-end-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Formato de Exportação */}
                <div className="space-y-2">
                  <Label>Formato de Exportação *</Label>
                  <Select value={exportFormat} onValueChange={setExportFormat}>
                    <SelectTrigger data-testid="format-select">
                      <SelectValue placeholder="Selecione o formato" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PDF">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-red-500" />
                          PDF
                        </div>
                      </SelectItem>
                      <SelectItem value="EXCEL">
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4 text-green-500" />
                          Excel
                        </div>
                      </SelectItem>
                      <SelectItem value="CSV">
                        <div className="flex items-center gap-2">
                          <Table2 className="w-4 h-4 text-blue-500" />
                          CSV
                        </div>
                      </SelectItem>
                      <SelectItem value="HTML">
                        <div className="flex items-center gap-2">
                          <Layout className="w-4 h-4 text-purple-500" />
                          HTML
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Grupo/Categoria (opcional) */}
                <div className="space-y-2">
                  <Label htmlFor="category-filter">Grupo de Produtos (opcional)</Label>
                  <Input
                    id="category-filter"
                    placeholder="Deixe em branco para todos"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    data-testid="category-filter-input"
                  />
                </div>

                {/* Info box para Performance de Cardápio */}
                {selectedReport === 'performance_cardapio' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Sobre este Relatório
                    </h4>
                    <p className="text-sm text-blue-700">
                      Este relatório classifica seus produtos em 4 categorias baseadas em popularidade e rentabilidade:
                    </p>
                    <ul className="text-sm text-blue-700 mt-2 space-y-1">
                      <li className="flex items-center gap-2">
                        <span className="w-3 h-3 bg-green-400 rounded-full"></span>
                        <strong>Estrela:</strong> Alta popularidade + Alta rentabilidade
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-3 h-3 bg-yellow-400 rounded-full"></span>
                        <strong>Cavalo:</strong> Alta popularidade + Baixa rentabilidade
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-3 h-3 bg-blue-400 rounded-full"></span>
                        <strong>Quebra-cabeça:</strong> Baixa popularidade + Alta rentabilidade
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-3 h-3 bg-red-400 rounded-full"></span>
                        <strong>Abacaxi:</strong> Baixa popularidade + Baixa rentabilidade
                      </li>
                    </ul>
                  </div>
                )}

                {/* Botão Gerar */}
                <Button 
                  onClick={handleGenerateReport}
                  disabled={generating}
                  className="w-full bg-red-500 hover:bg-red-600 text-white"
                  size="lg"
                  data-testid="generate-report-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      Gerar Relatório
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
            </div>
          )}

          {activeTab === 'graficos' && (
            <Card>
              <CardContent className="p-12 text-center">
                <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700">Gráficos Interativos</h3>
                <p className="text-gray-500 mt-2">Em breve: Visualize seus dados com gráficos dinâmicos</p>
              </CardContent>
            </Card>
          )}

          {activeTab === 'excel' && (
            <Card>
              <CardContent className="p-12 text-center">
                <FileSpreadsheet className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700">Exportação Excel Avançada</h3>
                <p className="text-gray-500 mt-2">Em breve: Exporte relatórios customizados para Excel</p>
              </CardContent>
            </Card>
          )}

          {activeTab === 'dashboard' && (
            <Card>
              <CardContent className="p-12 text-center">
                <Layout className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700">Dashboard Analítico</h3>
                <p className="text-gray-500 mt-2">Em breve: Painel com indicadores em tempo real</p>
              </CardContent>
            </Card>
          )}

          {activeTab === 'andamento' && (
            <div className="space-y-6">
              {/* Header com refresh */}
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">Relatórios em Andamento</h2>
                <Button variant="outline" size="sm" onClick={fetchJobs} disabled={loading}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </div>

              {/* Em processamento */}
              {inProgressJobs.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-500 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                    Processando ({inProgressJobs.length})
                  </h3>
                  <div className="grid gap-3">
                    {inProgressJobs.map(job => (
                      <ReportJobCard 
                        key={job.id} 
                        job={job} 
                        onView={handleViewReport}
                        onCancel={handleCancelReport}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Concluídos */}
              {completedJobs.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-500 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Concluídos ({completedJobs.length})
                  </h3>
                  <div className="grid gap-3">
                    {completedJobs.map(job => (
                      <ReportJobCard 
                        key={job.id} 
                        job={job} 
                        onView={handleViewReport}
                        onCancel={handleCancelReport}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Erros */}
              {errorJobs.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-500 flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-500" />
                    Com Erro ({errorJobs.length})
                  </h3>
                  <div className="grid gap-3">
                    {errorJobs.map(job => (
                      <ReportJobCard 
                        key={job.id} 
                        job={job} 
                        onView={handleViewReport}
                        onCancel={handleCancelReport}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Vazio */}
              {jobs.length === 0 && !loading && (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-700">Nenhum relatório gerado</h3>
                    <p className="text-gray-500 mt-2">Gere seu primeiro relatório na aba "Relatórios"</p>
                    <Button 
                      className="mt-4" 
                      onClick={() => setActiveTab('relatorios')}
                    >
                      Gerar Relatório
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    </MainLayout>
  );
}
